import { useEffect, useRef, useState, useCallback } from 'react'
import { Accessify, type AccessifyConfig, type AccessifyState } from '../core'

export type AccessifyWidgetProps = AccessifyConfig

export function AccessifyWidget(props: AccessifyWidgetProps) {
  const instanceRef = useRef<Accessify | null>(null)
  const propsRef = useRef<AccessifyWidgetProps>(props)
  propsRef.current = props

  useEffect(() => {
    const instance = new Accessify({
      ...propsRef.current,
      onLangChange: (newLang) => {
        propsRef.current.onLangChange?.(newLang)
      },
      onColorSchemeChange: (newScheme) => {
        propsRef.current.onColorSchemeChange?.(newScheme)
      },
      onSizeChange: (newSize) => {
        propsRef.current.onSizeChange?.(newSize)
      },
    })
    instanceRef.current = instance
    instance.mount()

    return () => {
      instance.destroy()
      instanceRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!instanceRef.current) return
    instanceRef.current.updateConfig({
      colorScheme: props.colorScheme,
      lang: props.lang,
      size: props.size,
      triggerScheme: props.triggerScheme,
      triggerIcon: props.triggerIcon,
      brandTitle: props.brandTitle,
    })
  }, [
    props.colorScheme,
    props.lang,
    props.size,
    props.triggerScheme,
    props.triggerIcon,
    props.brandTitle,
  ])

  return null
}

export function useAccessify(config: AccessifyConfig = {}) {
  const instanceRef = useRef<Accessify | null>(null)
  const configRef = useRef<AccessifyConfig>(config)
  configRef.current = config
  const [state, setState] = useState<AccessifyState | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [lang, setLangState] = useState(config.lang ?? 'en')

  useEffect(() => {
    const instance = new Accessify({
      ...configRef.current,
      onLangChange: (newLang) => {
        setLangState(newLang)
        configRef.current.onLangChange?.(newLang)
      },
    })
    instanceRef.current = instance
    instance.mount()
    setState(instance.getState())
    return () => {
      instance.destroy()
      instanceRef.current = null
    }
  }, [])

  const open = useCallback(() => {
    instanceRef.current?.open()
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    instanceRef.current?.close()
    setIsOpen(false)
  }, [])

  const toggle = useCallback(() => {
    instanceRef.current?.toggle()
    setIsOpen(prev => !prev)
  }, [])

  const reset = useCallback(() => {
    instanceRef.current?.reset()
    if (instanceRef.current) {
      setState(instanceRef.current.getState())
    }
  }, [])

  const setLang = useCallback((l: import('../core').Lang) => {
    instanceRef.current?.setLang(l)
    setLangState(l)
  }, [])

  const setTextColor = useCallback((c: string | null) => {
    instanceRef.current?.setTextColor(c)
    if (instanceRef.current) setState(instanceRef.current.getState())
  }, [])

  const setTitleColor = useCallback((c: string | null) => {
    instanceRef.current?.setTitleColor(c)
    if (instanceRef.current) setState(instanceRef.current.getState())
  }, [])

  const setBackgroundColor = useCallback((c: string | null) => {
    instanceRef.current?.setBackgroundColor(c)
    if (instanceRef.current) setState(instanceRef.current.getState())
  }, [])

  return { open, close, toggle, reset, state, isOpen, lang, setLang, setTextColor, setTitleColor, setBackgroundColor }
}

export type { AccessifyConfig, AccessifyState, Lang, SupportedLang } from '../core'
export { Accessify, SUPPORTED_LANGS } from '../core'
